import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { RealtimeChatService, ChatMessage, TypingIndicator, UserPresence } from '@/services/realtimeChatService';
import { useToast } from '@/hooks/use-toast';

interface ChatContextType {
  messages: ChatMessage[];
  onlineUsers: UserPresence[];
  typingUsers: TypingIndicator[];
  connectionStatus: 'connecting' | 'connected' | 'disconnected';
  queuedMessages: number;
  isLoading: boolean;
  sendMessage: (recipientId: string, content: string) => Promise<void>;
  markAsRead: (messageId: string) => Promise<void>;
  sendTypingIndicator: (conversationId: string, isTyping: boolean) => void;
  isUserOnline: (userId: string) => boolean;
  isUserTyping: (userId: string, conversationId: string) => boolean;
  getMessageDeliveryStatus: (messageId: string) => string;
}

const ChatContext = createContext<ChatContextType | null>(null);

export const ChatProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const location = useLocation();
  const chatService = useRef<RealtimeChatService | null>(null);
  const isInitialized = useRef(false);
  const connectionAttempts = useRef(0);
  const lastNotificationTime = useRef(0);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const disconnectTimerRef = useRef<NodeJS.Timeout | null>(null);

  const [state, setState] = useState({
    messages: [] as ChatMessage[],
    onlineUsers: [] as UserPresence[],
    typingUsers: [] as TypingIndicator[],
    connectionStatus: 'disconnected' as 'connecting' | 'connected' | 'disconnected',
    queuedMessages: 0,
    isLoading: false
  });

  // Debounced toast notification
  const showToast = useCallback((title: string, description: string, variant?: 'default' | 'destructive') => {
    const now = Date.now();
    if (now - lastNotificationTime.current < 5000) return; // Debounce 5 seconds
    
    lastNotificationTime.current = now;
    toast({ title, description, variant, duration: 3000 });
  }, [toast]);

  // Auto-disconnect when leaving chat routes
  const isChatRoute = location.pathname === '/chat' || location.pathname === '/networking';
  
  useEffect(() => {
    // Clear any pending disconnect timer when route changes
    if (disconnectTimerRef.current) {
      clearTimeout(disconnectTimerRef.current);
      disconnectTimerRef.current = null;
    }

    // If we're NOT on a chat route and chat is connected, schedule disconnect
    if (!isChatRoute && chatService.current && state.connectionStatus === 'connected') {
      console.log('🔌 Scheduling chat disconnect (left chat routes)');
      disconnectTimerRef.current = setTimeout(async () => {
        console.log('🔌 Disconnecting chat service');
        await chatService.current?.disconnect();
        chatService.current = null;
        isInitialized.current = false;
        setState(prev => ({ 
          ...prev, 
          connectionStatus: 'disconnected',
          isLoading: false 
        }));
      }, 2000); // 2 seconds delay to avoid rapid disconnects during quick navigation
    }
    
    // If we're back on a chat route and currently disconnected, reconnect
    if (isChatRoute && chatService.current && state.connectionStatus === 'disconnected' && isInitialized.current) {
      console.log('🔌 Reconnecting chat (back to chat routes)');
      chatService.current.connect(user?.id || '');
    }

    return () => {
      if (disconnectTimerRef.current) {
        clearTimeout(disconnectTimerRef.current);
      }
    };
  }, [isChatRoute, state.connectionStatus, user?.id]);

  // Initialize chat service once
  useEffect(() => {
    if (!user?.id || isInitialized.current || !isChatRoute) return;

    const initializeChat = async () => {
      if (chatService.current) {
        await chatService.current.disconnect();
      }

      chatService.current = new RealtimeChatService(user.id);
      setState(prev => ({ ...prev, isLoading: true }));

      try {
        // Set up event listeners
        chatService.current.onMessage((message: ChatMessage) => {
          setState(prev => ({
            ...prev,
            messages: [...prev.messages.filter(m => m.id !== message.id), message]
              .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
          }));

          // Show notification for new messages (only if not from current user)
          if (message.sender_id !== user.id && message.delivery_status === 'sent') {
            showToast(
              "Nouveau message",
              message.content.substring(0, 50) + (message.content.length > 50 ? '...' : '')
            );
          }
        });

        chatService.current.onTyping((typing: TypingIndicator) => {
          setState(prev => ({
            ...prev,
            typingUsers: typing.is_typing 
              ? [...prev.typingUsers.filter(t => t.user_id !== typing.user_id), typing]
              : prev.typingUsers.filter(t => t.user_id !== typing.user_id)
          }));
        });

        chatService.current.onPresence((presence: UserPresence[]) => {
          setState(prev => ({
            ...prev,
            onlineUsers: presence
          }));
        });

        chatService.current.onConnection((status: string) => {
          setState(prev => ({
            ...prev,
            connectionStatus: status as 'connecting' | 'connected' | 'disconnected',
            queuedMessages: chatService.current?.getQueuedMessageCount() || 0
          }));

          // Smart notifications based on connection attempts
          if (status === 'connected') {
            connectionAttempts.current = 0;
            // Toast removed - silent connection for better UX
          } else if (status === 'disconnected') {
            connectionAttempts.current++;
            // Only show error if multiple failed attempts and user is active
            if (connectionAttempts.current > 2 && document.visibilityState === 'visible') {
              showToast(
                "Chat déconnecté", 
                "Vérifiez votre connexion internet", 
                "destructive"
              );
            }
          }
        });

        // Connect to chat
        await chatService.current.connect(user.id);
        isInitialized.current = true;

      } catch (error) {
        console.error('Failed to initialize realtime chat:', error);
        if (document.visibilityState === 'visible') {
          showToast(
            "Erreur de connexion",
            "Impossible de connecter le chat temps réel",
            "destructive"
          );
        }
      } finally {
        setState(prev => ({ ...prev, isLoading: false }));
      }
    };

    initializeChat();

    // Cleanup on unmount or user change
    return () => {
      if (chatService.current) {
        chatService.current.disconnect();
        chatService.current = null;
      }
      isInitialized.current = false;
    };
  }, [user?.id, showToast, isChatRoute]);

  // Send message
  const sendMessage = useCallback(async (recipientId: string, content: string): Promise<void> => {
    if (!user?.id || !content.trim() || !chatService.current) return;

    try {
      await chatService.current.sendMessage({
        sender_id: user.id,
        recipient_id: recipientId,
        content: content.trim(),
        is_read: false
      });
    } catch (error) {
      console.error('Failed to send message:', error);
      showToast(
        "Erreur d'envoi",
        "Le message sera envoyé dès la reconnexion",
        "destructive"
      );
    }
  }, [user?.id, showToast]);

  // Mark message as read
  const markAsRead = useCallback(async (messageId: string): Promise<void> => {
    if (!chatService.current) return;
    
    try {
      await chatService.current.markAsRead(messageId);
    } catch (error) {
      console.error('Failed to mark message as read:', error);
    }
  }, []);

  // Send typing indicator
  const sendTypingIndicator = useCallback((conversationId: string, isTyping: boolean) => {
    if (!user?.id || !chatService.current) return;

    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    chatService.current.sendTypingIndicator(conversationId, isTyping);

    // Auto-stop typing after 3 seconds
    if (isTyping) {
      typingTimeoutRef.current = setTimeout(() => {
        chatService.current?.sendTypingIndicator(conversationId, false);
      }, 3000);
    }
  }, [user?.id]);

  // Check if user is online
  const isUserOnline = useCallback((userId: string): boolean => {
    return state.onlineUsers.some(user => 
      user.user_id === userId && 
      user.status === 'online' &&
      new Date().getTime() - new Date(user.online_at).getTime() < 5 * 60 * 1000 // 5 minutes
    );
  }, [state.onlineUsers]);

  // Check if user is typing in conversation
  const isUserTyping = useCallback((userId: string, conversationId: string): boolean => {
    return state.typingUsers.some(typing => 
      typing.user_id === userId && 
      typing.conversation_id === conversationId && 
      typing.is_typing
    );
  }, [state.typingUsers]);

  // Get message delivery status
  const getMessageDeliveryStatus = useCallback((messageId: string): string => {
    const message = state.messages.find(m => m.id === messageId);
    return message?.delivery_status || 'unknown';
  }, [state.messages]);

  const contextValue: ChatContextType = {
    ...state,
    sendMessage,
    markAsRead,
    sendTypingIndicator,
    isUserOnline,
    isUserTyping,
    getMessageDeliveryStatus
  };

  return (
    <ChatContext.Provider value={contextValue}>
      {children}
    </ChatContext.Provider>
  );
};

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};